import { ClerkProvider } from "@clerk/clerk-react";
import type { PostHogConfig } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import type React from "react";
import { useEffect } from "react";
import { RouterProvider, createBrowserRouter, useLocation } from "react-router-dom";
import Authentication from "./components/Authentication";
import Header from "./components/Header";
import RoleGuard from "./components/RoleGuard";
import { CurrentUserProvider } from "./context/CurrentUserContext";
import AcceptInvite from "./pages/AcceptInvite";
import Account from "./pages/Account";
import AddTeam from "./pages/AddTeam";
import { CurrencyPage } from "./pages/Currency";
import Dashboard from "./pages/Dashboard";
import FAQ from "./pages/FAQ";
import InviteAccepted from "./pages/InviteAccepted";
import NewPayment from "./pages/NewPayment";
import Onboarding from "./pages/Onboarding";
import PaymentDetails from "./pages/PaymentDetails";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import { SettingsPage } from "./pages/Settings";
import Start from "./pages/Start";
import Team from "./pages/Team";
import { TermsAndConditionsPage } from "./pages/TermsAndConditions";

// Get publishable key from environment
const clerkPublishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const options: Partial<PostHogConfig> = {
	api_host: process.env.REACT_APP_POSTHOG_HOST ?? "https://eu.i.posthog.com",
	defaults: "2025-05-24",
	// Disable event capture in development so flags work without sending analytics
	autocapture: process.env.NODE_ENV === "production",
	capture_pageview: process.env.NODE_ENV === "production",
	capture_pageleave: process.env.NODE_ENV === "production",
};

if (!clerkPublishableKey) {
	throw new Error("Missing Publishable Key");
}

const ScrollToTop: React.FC = () => {
	const { pathname } = useLocation();
	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname is intentionally used to trigger scroll on route change
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);
	return null;
};

// Layout component that includes the header
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	return (
		<>
			<ScrollToTop />
			<Header />
			{children}
		</>
	);
};

const router = createBrowserRouter([
	{
		path: "/",
		element: (
			<Authentication>
				<Layout>
					<Dashboard />
				</Layout>
			</Authentication>
		),
	},
	{
		path: "/account",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<Account />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/start",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<Start />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/start/:accountId",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<Onboarding />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/payments/new",
		element: (
			<Authentication>
				<RoleGuard roles={['owner', 'member']}>
					<Layout>
						<NewPayment />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/payments/:id",
		element: (
			<Authentication>
				<RoleGuard roles={['owner', 'member']}>
					<Layout>
						<PaymentDetails />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/accept-invite",
		element: <AcceptInvite />,
	},
	{
		path: "/invite-accepted",
		element: <InviteAccepted />,
	},
	{
		path: "/pay/:id",
		element: <PaymentPage />,
	},
	{
		path: "/pay/:id/success",
		element: <PaymentSuccess />,
	},
	{
		path: "/settings",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<SettingsPage />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/team",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<Team />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/team/add",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<AddTeam />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/settings/currency",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<CurrencyPage />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/settings/termsandconditions",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<TermsAndConditionsPage />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
	{
		path: "/faq",
		element: (
			<Authentication>
				<RoleGuard roles={['owner']}>
					<Layout>
						<FAQ />
					</Layout>
				</RoleGuard>
			</Authentication>
		),
	},
]);

const App: React.FC = () => {
	return (
		<PostHogProvider
			apiKey={process.env.REACT_APP_POSTHOG_KEY as unknown as string}
			options={options}
		>
			<ClerkProvider publishableKey={clerkPublishableKey}>
				<CurrentUserProvider>
					<RouterProvider router={router} />
				</CurrentUserProvider>
			</ClerkProvider>
		</PostHogProvider>
	);
};

export default App;
